/**
 * Sprint ELC-5.4 — RBAC helper hook for ELC catalog pages.
 *
 * Reads the user's permission slugs once and returns derived booleans
 * for the actions Role Catalog + Department Catalog gate on. Same
 * slug map the server-side `require_permission(...)` decorators
 * enforce — UI gates are defence-in-depth.
 *
 * Each capability returns the disabled reason when the user lacks
 * the slug so the context menu can render the tooltip directly.
 */
import { useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';

export interface ElcPermissionCheck {
  /** True when the user holds the required slug. */
  allowed: boolean;
  /** Tooltip shown on the disabled menu item. */
  disabledReason: string;
}

export interface ElcPermissions {
  manageDepartments: ElcPermissionCheck;
  publishDepartments: ElcPermissionCheck;
  manageRoles: ElcPermissionCheck;
  publishRoles: ElcPermissionCheck;
  assignRoles: ElcPermissionCheck;
  manageLocations: ElcPermissionCheck;
  manageAccessCatalog: ElcPermissionCheck;
  manageSettings: ElcPermissionCheck;
}

function check(
  permissions: string[],
  slug: string,
): ElcPermissionCheck {
  return {
    allowed: permissions.includes(slug),
    disabledReason: `Requires \`${slug}\``,
  };
}

export function useElcPermissions(): ElcPermissions {
  const permissions = useAuthStore((s) => s.user?.permissions ?? []);
  return useMemo(
    () => ({
      manageDepartments: check(
        permissions,
        'elc-departments.manage.org',
      ),
      publishDepartments: check(
        permissions,
        'elc-departments.publish.org',
      ),
      manageRoles: check(permissions, 'elc-roles.manage.org'),
      publishRoles: check(permissions, 'elc-roles.publish.org'),
      assignRoles: check(permissions, 'elc-roles.assign.org'),
      manageLocations: check(permissions, 'elc-locations.manage.org'),
      manageAccessCatalog: check(permissions, 'elc-access-catalog.manage.org'),
      manageSettings: check(permissions, 'elc-settings.manage.org'),
    }),
    [permissions],
  );
}
